import type { Address } from "viem"
import type { Config } from "wagmi"
import { readContract } from "@wagmi/core"

import { getScaleSwapAmountsOut } from "@/domains/on-chain"
import { THICK_V1_BASE_ADDRESSES } from "@/domains/thick-v1/constants"
import { THICK_V2_BASE_ADDRESSES } from "@/domains/thick-v2/constants"
import type { TokenStore } from "@/domains/tokens"

import type { ScaleRouteCandidate } from "../services/scale-route-finder"

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000" as const

const classicRouterFactoryAbi = [
  {
    inputs: [],
    name: "factory",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
] as const

const classicFactoryAbi = [
  {
    inputs: [
      { internalType: "address", name: "", type: "address" },
      { internalType: "address", name: "", type: "address" },
      { internalType: "bool", name: "", type: "bool" },
    ],
    name: "getPair",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
] as const

const classicPairAbi = [
  {
    inputs: [],
    name: "getReserves",
    outputs: [
      { internalType: "uint256", name: "_reserve0", type: "uint256" },
      { internalType: "uint256", name: "_reserve1", type: "uint256" },
      { internalType: "uint256", name: "_blockTimestampLast", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
] as const

const thickV1QuoterAbi = [
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "tokenIn", type: "address" },
          { internalType: "address", name: "tokenOut", type: "address" },
          { internalType: "uint256", name: "amountIn", type: "uint256" },
          { internalType: "uint24", name: "fee", type: "uint24" },
          { internalType: "uint160", name: "sqrtPriceLimitX96", type: "uint160" },
        ],
        internalType: "struct IQuoterV2.QuoteExactInputSingleParams",
        name: "params",
        type: "tuple",
      },
    ],
    name: "quoteExactInputSingle",
    outputs: [
      { internalType: "uint256", name: "amountOut", type: "uint256" },
      { internalType: "uint160", name: "sqrtPriceX96After", type: "uint160" },
      { internalType: "uint32", name: "initializedTicksCrossed", type: "uint32" },
      { internalType: "uint256", name: "gasEstimate", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const

const thickV2QuoterAbi = [
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "tokenIn", type: "address" },
          { internalType: "address", name: "tokenOut", type: "address" },
          { internalType: "uint256", name: "amountIn", type: "uint256" },
          { internalType: "int24", name: "tickSpacing", type: "int24" },
          { internalType: "uint160", name: "sqrtPriceLimitX96", type: "uint160" },
        ],
        internalType: "struct IQuoterV2.QuoteExactInputSingleParams",
        name: "params",
        type: "tuple",
      },
    ],
    name: "quoteExactInputSingle",
    outputs: [
      { internalType: "uint256", name: "amountOut", type: "uint256" },
      { internalType: "uint160", name: "sqrtPriceX96After", type: "uint160" },
      { internalType: "uint32", name: "initializedTicksCrossed", type: "uint32" },
      { internalType: "uint256", name: "gasEstimate", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const

export class ScaleRouterOnChainTransport {
  private factoryAddress: Address | undefined

  constructor(
    private readonly config: Config,
    private readonly chainId: number,
    private readonly routerAddress: Address,
  ) {}

  async findDirectClassicRoutes(inputToken: TokenStore, outputToken: TokenStore): Promise<ScaleRouteCandidate[]> {
    const factoryAddress = await this.getClassicFactoryAddress()
    if (!factoryAddress) {
      return []
    }

    const routes = await Promise.all([
      this.findDirectClassicRoute(factoryAddress, inputToken, outputToken, false),
      this.findDirectClassicRoute(factoryAddress, inputToken, outputToken, true),
    ])

    return routes.filter((route): route is ScaleRouteCandidate => Boolean(route))
  }

  async quoteRoute(amountIn: bigint, route: ScaleRouteCandidate): Promise<bigint | null> {
    if (route.source === "thick-v1") {
      const result = await readContract(this.config, {
        address: THICK_V1_BASE_ADDRESSES.quoter,
        abi: thickV1QuoterAbi,
        functionName: "quoteExactInputSingle",
        chainId: this.chainId,
        args: [
          {
            tokenIn: route.hops[0]!.from,
            tokenOut: route.hops[0]!.to,
            amountIn,
            fee: route.clPool.feeTier,
            sqrtPriceLimitX96: 0n,
          },
        ],
      })
      return result[0]
    }

    if (route.source === "thick-v2") {
      if (!route.clPool.tickSpacing) return null
      const result = await readContract(this.config, {
        address: THICK_V2_BASE_ADDRESSES.quoterV2,
        abi: thickV2QuoterAbi,
        functionName: "quoteExactInputSingle",
        chainId: this.chainId,
        args: [
          {
            tokenIn: route.hops[0]!.from,
            tokenOut: route.hops[0]!.to,
            amountIn,
            tickSpacing: route.clPool.tickSpacing,
            sqrtPriceLimitX96: 0n,
          },
        ],
      })
      return result[0]
    }

    const amounts = await getScaleSwapAmountsOut({
      chainId: this.chainId,
      config: this.config,
      routerAddress: this.routerAddress,
      amountIn,
      routes: route.hops.map((hop) => ({
        from: hop.from,
        to: hop.to,
        stable: hop.stable,
      })),
    })

    return amounts[amounts.length - 1] ?? null
  }

  getRouterAddress(): Address {
    return this.routerAddress
  }

  private async getClassicFactoryAddress(): Promise<Address | undefined> {
    if (this.factoryAddress) {
      return this.factoryAddress
    }

    try {
      const factoryAddress = await readContract(this.config, {
        address: this.routerAddress,
        abi: classicRouterFactoryAbi,
        functionName: "factory",
        chainId: this.chainId,
      })
      this.factoryAddress = factoryAddress
      return factoryAddress
    } catch {
      return undefined
    }
  }

  private async findDirectClassicRoute(
    factoryAddress: Address,
    inputToken: TokenStore,
    outputToken: TokenStore,
    stable: boolean,
  ): Promise<ScaleRouteCandidate | null> {
    try {
      const pairAddress = await readContract(this.config, {
        address: factoryAddress,
        abi: classicFactoryAbi,
        functionName: "getPair",
        args: [inputToken.address.toViemAddress(), outputToken.address.toViemAddress(), stable],
        chainId: this.chainId,
      })

      if (pairAddress.toLowerCase() === ZERO_ADDRESS) {
        return null
      }

      const reserves = await readContract(this.config, {
        address: pairAddress,
        abi: classicPairAbi,
        functionName: "getReserves",
        chainId: this.chainId,
      })

      if (reserves[0] <= 0n || reserves[1] <= 0n) {
        return null
      }

      const poolType = stable ? "sAMM" : "vAMM"
      return {
        source: "classic-vamm-samm",
        id: `onchain-${pairAddress}-${inputToken.address.toString()}-${outputToken.address.toString()}-${stable ? "stable" : "volatile"}`,
        hops: [
          {
            from: inputToken.address.toViemAddress(),
            to: outputToken.address.toViemAddress(),
            stable,
            poolAddress: pairAddress,
            poolName: `${poolType}-${inputToken.symbol}/${outputToken.symbol}`,
          },
        ],
        pools: [],
      }
    } catch {
      return null
    }
  }
}
